'use client';

import type { FC } from 'react';
import { cva } from 'class-variance-authority';
import { cn } from '../../utils/cn';
import { FaSeedling, FaLeaf, FaFire, FaBolt, FaStar, FaTrophy, FaCrown, FaGem, FaShieldAlt, GiCrossedSwords, GiCutDiamond } from '../icons';
import type { IconType } from '../icons';

export type LevelColorClass =
  | 'levelSeedling'
  | 'levelPlant'
  | 'levelFire'
  | 'levelBolt'
  | 'levelStar'
  | 'levelTrophy'
  | 'levelCrown'
  | 'levelGem'
  | 'levelShield'
  | 'levelSword'
  | 'levelDiamond';

export const getLevelColor = (level: number): LevelColorClass => {
  if (level >= 90) return 'levelDiamond';
  if (level >= 80) return 'levelSword';
  if (level >= 70) return 'levelShield';
  if (level >= 60) return 'levelGem';
  if (level >= 50) return 'levelCrown';
  if (level >= 40) return 'levelTrophy';
  if (level >= 30) return 'levelStar';
  if (level >= 20) return 'levelBolt';
  if (level >= 10) return 'levelFire';
  if (level >= 5) return 'levelPlant';
  return 'levelSeedling';
};

const LEVEL_GRADIENT_CLASS: Record<LevelColorClass, string> = {
  levelSeedling: 'bg-[image:var(--level-seedling-gradient)]',
  levelPlant: 'bg-[image:var(--level-plant-gradient)]',
  levelFire: 'bg-[image:var(--level-fire-gradient)]',
  levelBolt: 'bg-[image:var(--level-bolt-gradient)]',
  levelStar: 'bg-[image:var(--level-star-gradient)]',
  levelTrophy: 'bg-[image:var(--level-trophy-gradient)]',
  levelCrown: 'bg-[image:var(--level-crown-gradient)]',
  levelGem: 'bg-[image:var(--level-gem-gradient)]',
  levelShield: 'bg-[image:var(--level-shield-gradient)]',
  levelSword: 'bg-[image:var(--level-sword-gradient)]',
  levelDiamond: 'bg-[image:var(--level-diamond-gradient)]',
};

export const getLevelGradientClass = (level: number): string => LEVEL_GRADIENT_CLASS[getLevelColor(level)];

const LEVEL_MOSAIC_COLOR_CLASS: Record<LevelColorClass, string> = {
  levelSeedling: 'text-[var(--level-seedling-color)]',
  levelPlant: 'text-[var(--level-plant-color)]',
  levelFire: 'text-[var(--level-fire-color)]',
  levelBolt: 'text-[var(--level-bolt-color)]',
  levelStar: 'text-[var(--level-star-color)]',
  levelTrophy: 'text-[var(--level-trophy-color)]',
  levelCrown: 'text-[var(--level-crown-color)]',
  levelGem: 'text-[var(--level-gem-color)]',
  levelShield: 'text-[var(--level-shield-color)]',
  levelSword: 'text-[var(--level-sword-color)]',
  levelDiamond: 'text-[var(--level-diamond-color)]',
};

export const getLevelMosaicColorClass = (level: number): string => LEVEL_MOSAIC_COLOR_CLASS[getLevelColor(level)];

export const getLevelIcon = (level: number): IconType => {
  if (level >= 90) return GiCutDiamond;
  if (level >= 80) return GiCrossedSwords;
  if (level >= 70) return FaShieldAlt;
  if (level >= 60) return FaGem;
  if (level >= 50) return FaCrown;
  if (level >= 40) return FaTrophy;
  if (level >= 30) return FaStar;
  if (level >= 20) return FaBolt;
  if (level >= 10) return FaFire;
  if (level >= 5) return FaLeaf;
  return FaSeedling;
};

const levelBadgeVariants = cva(
  'inline-flex items-center justify-center gap-1 rounded-full font-bold text-white border-none cursor-default',
  {
    variants: {
      size: {
        small: 'px-2 py-1 text-xs [&_.level-icon]:text-xs',
        medium: 'px-3 py-1.5 text-xs [&_.level-icon]:text-xs',
        large: 'px-4 py-2 text-sm [&_.level-icon]:text-base',
      },
      color: {
        levelSeedling: 'bg-[image:var(--level-seedling-gradient)]',
        levelPlant: 'bg-[image:var(--level-plant-gradient)]',
        levelFire: 'bg-[image:var(--level-fire-gradient)]',
        levelBolt: 'bg-[image:var(--level-bolt-gradient)]',
        levelStar: 'bg-[image:var(--level-star-gradient)]',
        levelTrophy: 'bg-[image:var(--level-trophy-gradient)]',
        levelCrown: 'bg-[image:var(--level-crown-gradient)]',
        levelGem: 'bg-[image:var(--level-gem-gradient)]',
        levelShield: 'bg-[image:var(--level-shield-gradient)]',
        levelSword: 'bg-[image:var(--level-sword-gradient)]',
        levelDiamond: 'bg-[image:var(--level-diamond-gradient)]',
      },
    },
    defaultVariants: {
      size: 'medium',
    },
  }
);

interface LevelBadgeProps {
  level: number;
  size?: 'small' | 'medium' | 'large';
  showIcon?: boolean;
  showText?: boolean;
  showLabel?: boolean;
  className?: string;
  onClick?: () => void;
}

export const LevelBadge: FC<LevelBadgeProps> = ({
  level,
  size = 'medium',
  showIcon = true,
  showText = true,
  showLabel = false,
  className,
  onClick,
}) => {
  const colorClass = getLevelColor(level);
  const Icon = getLevelIcon(level);

  const Component = onClick ? 'button' : 'div';

  return (
    <Component
      className={cn(
        levelBadgeVariants({ size, color: colorClass }),
        onClick && 'cursor-pointer transition-opacity duration-200 ease-smooth active:opacity-90',
        className,
      )}
      onClick={onClick}
    >
      {showIcon && <Icon className="level-icon shrink-0" />}
      {showText && <span className="whitespace-nowrap">{showLabel && 'LVL '}{level}</span>}
    </Component>
  );
};
